/**
 * @fileoverview Pre-Appointment pipeline job side panel (MOR-882)
 *
 * Sheet opened from a pipeline card/row, in two tabs:
 *
 *  - **Details** — the job's pipeline fields (stage, labels, referrer), its
 *    contacts so a director's record and correspondence are one click away, and
 *    the firm-wide conflict checks (run a new check, watch the live tally, vote
 *    in-app, close).
 *  - **Notes** — the job's chronological notes feed, so a pitch can be
 *    journalled as it develops.
 *
 * DIRRI drafting is moving to the job's Reporting section, so it no longer
 * lives here.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  BellRing,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  Handshake,
  Loader2,
  Lock,
  Mail,
  Paperclip,
  Plus,
  Users,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Undo2,
  X,
  XCircle,
} from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import {
  APPOINTMENT_TYPE_LABELS,
  type PipelineJob,
  type PipelineLabel,
  type PipelineReferrer,
  type PipelineStatus,
} from "./constants";
import { JOB_STATUS_LABELS, JOB_STATUS_VARIANTS } from "@/lib/job-statuses";
import {
  CONTACT_TYPE_META,
  ROLE_META,
  distinctRoles,
  type JobContactRow,
} from "@/components/jobs/phases/contacts/types";
import { Badge, type BadgeColor } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Textarea } from "@/ui/primitives/textarea";
import { Separator } from "@/ui/primitives/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { ScrollArea } from "@/ui/primitives/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/ui/composed/overlays/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/ui/composed/overlays/alert-dialog";
import { MultiSearchableSelect } from "@/ui/composed/inputs/multi-searchable-select";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { JobNotesFeed } from "@/components/jobs/notes/JobNotesFeed";
import {
  JobChatDocumentPicker,
  type ChatDocPick,
} from "@/components/agents/universal/ChatDocumentPicker";
import { DocumentDetail } from "@/components/jobs/phases/gather/documents/DocumentDetail";
import { useConflictBriefDraft } from "./use-conflict-brief-draft";
import { AddReferrerDialog } from "./add-referrer-dialog";
import { ConflictEntityPicker } from "./conflict-entity-picker";
import {
  toEntityInputs,
  type ConflictEntityDraft,
} from "./conflict-entity-picker.helpers";
import { ConflictVoteBar } from "./conflict-vote-bar";
import { ConflictBriefView } from "./conflict-brief-view";
import { ConflictPartyGroup } from "./conflict-party-group";
import { ConflictPartySchedule } from "./conflict-party-schedule";
import { ConflictRecipientPicker } from "./conflict-recipient-picker";
import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import {
  deriveKeyContactRows,
  describeReminder,
  describeWithdrawal,
  findMyResponse,
  formatClosedToggleLabel,
  formatResendToastMessage,
  formatVoteHistoryLine,
  splitConflictChecksByStatus,
  visibleConflictResponses,
  voteLabel,
  votedResponses,
  type ConflictCheck,
} from "./pipelineJobModel";

const NO_STAGE = "__none__";
const NO_REFERRER = "__none__";

export function PipelineJobPanel({
  job,
  statuses,
  labels,
  referrers,
  onClose,
  onOpenReferrer,
  onOpenContact,
  orgId,
  canCreateReferrer,
}: {
  job: PipelineJob | null;
  statuses: PipelineStatus[];
  labels: PipelineLabel[];
  referrers: PipelineReferrer[];
  onClose: () => void;
  /** Open the referrer's firm-contact drawer (mail's contact-panel pattern). */
  onOpenReferrer: (firmContactId: Id<"firmContacts">) => void;
  /** Open a job contact's record, where its correspondence is surfaced. */
  onOpenContact: (contactId: Id<"jobContacts">) => void;
  orgId: Id<"organizations">;
  /** Firm-directory writes are owner/admin only. */
  canCreateReferrer: boolean;
}) {
  const navigate = useNavigate();
  const [addReferrerOpen, setAddReferrerOpen] = useState(false);
  const setPipelineStatus = useMutation(
    api.preAppointment.pipeline.setPipelineStatus,
  );
  const setLabels = useMutation(api.preAppointment.pipeline.setLabels);
  const setReferrer = useMutation(api.preAppointment.pipeline.setReferrer);

  const labelOptions = useMemo(
    () =>
      labels.map((l) => ({
        value: String(l._id),
        label: l.name,
        color: l.color as BadgeColor,
      })),
    [labels],
  );
  const referrerOptions = useMemo(
    () => [
      { value: NO_REFERRER, label: "No referrer" },
      // MOR-995 — a person nested under a referring firm shows as
      // "Person — Firm" so referrers from the same firm stay distinguishable.
      ...referrers.map((r) => ({
        value: String(r._id),
        label: r.parentDisplayName
          ? `${r.displayName} — ${r.parentDisplayName}`
          : r.displayName,
      })),
    ],
    [referrers],
  );

  const onError = (fallback: string) => (err: unknown) =>
    toast.error(extractErrorMessage(err, fallback));

  return (
    <Sheet open={job !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
        {job && (
          <>
            <SheetHeader className="border-b p-4">
              <div className="flex items-start justify-between gap-2 pr-8">
                <div className="min-w-0">
                  <SheetTitle className="truncate">{job.entityName}</SheetTitle>
                  <SheetDescription className="flex flex-wrap items-center gap-1.5 pt-1">
                    <Badge variant={JOB_STATUS_VARIANTS[job.status]} size="sm">
                      {JOB_STATUS_LABELS[job.status]}
                    </Badge>
                    {job.appointmentType && (
                      <Badge variant="outline" size="sm">
                        {APPOINTMENT_TYPE_LABELS[job.appointmentType]}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      Created {format(job._creationTime, "d MMM yyyy")}
                    </span>
                  </SheetDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-1.5"
                  onClick={() =>
                    navigate({
                      to: "/dashboard/jobs/$jobId/plan",
                      params: { jobId: job._id },
                    })
                  }
                >
                  <ExternalLink className="size-3.5" />
                  Open job
                </Button>
              </div>
            </SheetHeader>

            {/* Keyed on the job so opening another card lands on Details with
                a fresh notes composer rather than the last job's tab. */}
            <Tabs
              key={String(job._id)}
              defaultValue="details"
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="px-4 pt-3">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="notes">Notes</TabsTrigger>
                </TabsList>
              </div>

              {/* The viewport's injected `display: table` wrapper sizes to the
                  content's max-content width and blows out past the sheet —
                  force it back to a block that fills the panel. */}
              <ScrollArea className="min-h-0 flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:w-full">
                <TabsContent value="details" className="mt-0">
                  {/*
                    `w-full min-w-0` pins the content to the viewport width: one
                    unshrinkable row (a button strip, a long name) would
                    otherwise widen this wrapper and clip everything else on the
                    right.
                  */}
                  <div className="flex w-full min-w-0 flex-col gap-5 p-4">
                    {/* Pipeline fields */}
                    <div className="grid gap-4">
                      <div className="grid gap-1.5">
                        <Label>Pipeline stage</Label>
                        <Select
                          value={
                            job.preAppointmentStatusId
                              ? String(job.preAppointmentStatusId)
                              : NO_STAGE
                          }
                          onValueChange={(value) =>
                            void setPipelineStatus({
                              jobId: job._id,
                              statusId:
                                value === NO_STAGE
                                  ? null
                                  : (value as Id<"preAppointmentStatuses">),
                            }).catch(onError("Could not update the stage."))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a stage" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NO_STAGE}>No stage</SelectItem>
                            {statuses.map((s) => (
                              <SelectItem
                                key={String(s._id)}
                                value={String(s._id)}
                              >
                                <span className="flex items-center gap-2">
                                  <Badge
                                    variant="soft"
                                    size="sm"
                                    color={s.color as BadgeColor}
                                  >
                                    {s.name}
                                  </Badge>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid gap-1.5">
                        <Label>Labels</Label>
                        <MultiSearchableSelect
                          value={job.preAppointmentLabelIds.map(String)}
                          onValueChange={(value) =>
                            void setLabels({
                              jobId: job._id,
                              labelIds: value as Id<"preAppointmentLabels">[],
                            }).catch(onError("Could not update the labels."))
                          }
                          options={labelOptions}
                          placeholder="Add labels…"
                        />
                      </div>

                      <div className="grid gap-1.5">
                        <Label className="flex items-center gap-1.5">
                          <Handshake className="size-3.5" />
                          Referrer
                        </Label>
                        <div className="flex items-center gap-2">
                          <SearchableSelect
                            value={
                              job.referrer
                                ? String(job.referrer._id)
                                : NO_REFERRER
                            }
                            onValueChange={(value) =>
                              void setReferrer({
                                jobId: job._id,
                                referrerFirmContactId:
                                  value === NO_REFERRER
                                    ? null
                                    : (value as Id<"firmContacts">),
                              }).catch(
                                onError("Could not update the referrer."),
                              )
                            }
                            options={referrerOptions}
                            placeholder="Tag a referrer…"
                            searchPlaceholder="Search referrers…"
                            emptyMessage="No referrer contacts yet — add one with the + button."
                            className="flex-1"
                          />
                          {canCreateReferrer && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="shrink-0 gap-1"
                              onClick={() => setAddReferrerOpen(true)}
                              title="Add a new referrer"
                            >
                              <Plus className="size-3.5" />
                              New
                            </Button>
                          )}
                        </div>
                        {job.referrer && (
                          <button
                            type="button"
                            className="flex w-fit items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                            onClick={() =>
                              job.referrer && onOpenReferrer(job.referrer._id)
                            }
                          >
                            <Handshake className="size-3" />
                            View {job.referrer.displayName}'s details
                          </button>
                        )}
                      </div>

                      <CustomFieldsSection orgId={orgId} job={job} />
                    </div>

                    <Separator />

                    <JobContactsSection
                      jobId={job._id}
                      onOpenContact={onOpenContact}
                    />

                    <Separator />

                    <ConflictChecksSection
                      jobId={job._id}
                      entityName={job.entityName}
                      orgId={orgId}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="notes" className="mt-0">
                  <div className="flex w-full min-w-0 flex-col p-4">
                    <JobNotesFeed jobId={job._id} />
                  </div>
                </TabsContent>
              </ScrollArea>
            </Tabs>

            <AddReferrerDialog
              orgId={orgId}
              open={addReferrerOpen}
              onOpenChange={setAddReferrerOpen}
              onCreated={(firmContactId) =>
                void setReferrer({
                  jobId: job._id,
                  referrerFirmContactId: firmContactId,
                }).catch(onError("Could not tag the new referrer."))
              }
            />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------
// Custom fields (MOR-948)
// ---------------------------------------------------------------------------

/**
 * The firm's custom pipeline fields (e.g. a fee estimate), rendered among
 * the built-in Details fields. Values commit on blur / Enter (text and
 * numeric types) or on pick (dates); an emptied input clears the value.
 */
function CustomFieldsSection({
  orgId,
  job,
}: {
  orgId: Id<"organizations">;
  job: PipelineJob;
}) {
  const { data: fields } = useQuery(
    convexQuery(api.preAppointment.fields.listByOrg, { orgId }),
  );
  const setJobValue = useMutation(api.preAppointment.fields.setJobValue);

  if (!fields || fields.length === 0) return null;

  const handleSave = (
    fieldId: Id<"preAppointmentFields">,
    raw: string,
    current: string,
  ) => {
    const value = raw.trim();
    if (value === current) return;
    void setJobValue({
      jobId: job._id,
      fieldId,
      value: value || null,
    }).catch((err) =>
      toast.error(extractErrorMessage(err, "Could not save the field.")),
    );
  };

  return (
    <>
      {fields.map((field) => {
        const value = job.preAppointmentCustomFields[field._id] ?? "";
        return (
          <div key={String(field._id)} className="grid gap-1.5">
            <Label htmlFor={`custom-field-${String(field._id)}`}>
              {field.name}
            </Label>
            {field.type === "date" ? (
              <DatePicker
                value={value || undefined}
                onChange={(next) => handleSave(field._id, next, value)}
                placeholder="Pick a date"
              />
            ) : (
              <div className="relative">
                {field.type === "currency" && (
                  <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-muted-foreground">
                    $
                  </span>
                )}
                <Input
                  id={`custom-field-${String(field._id)}`}
                  // Remount when the stored value changes so the uncommitted
                  // draft never fights a save landing from elsewhere.
                  key={`${String(job._id)}-${value}`}
                  defaultValue={value}
                  inputMode={field.type === "text" ? undefined : "decimal"}
                  className={field.type === "currency" ? "pl-7" : undefined}
                  placeholder={field.type === "text" ? "Add a value…" : "0"}
                  onBlur={(e) => handleSave(field._id, e.target.value, value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.currentTarget.blur();
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Conflict checks
// ---------------------------------------------------------------------------

/** Rows rendered inline before the "View all" link takes over. */
const CONTACT_PREVIEW_LIMIT = 6;

/**
 * The job's directors, current before former. Opening one shows the full
 * contact record, whose Mail & Notes tab is the point of this section: the
 * practitioner can read the correspondence with a director without leaving
 * the pipeline.
 *
 * Rows come from the reconcile pipeline, which builds them out of what the
 * agents have extracted — so a pitch with no processed documents has none yet.
 */
function JobContactsSection({
  jobId,
  onOpenContact,
}: {
  jobId: Id<"jobs">;
  onOpenContact: (contactId: Id<"jobContacts">) => void;
}) {
  const navigate = useNavigate();
  const { data: contacts } = useQuery(
    convexQuery(api.contacts.queries.listJobContacts, { jobId }),
  );

  const rows = useMemo(
    () => deriveKeyContactRows(contacts as Array<JobContactRow> | undefined),
    [contacts],
  );

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <Users className="size-4" />
          Key Contacts
        </h3>
        {rows.length > 0 && (
          <Badge variant="secondary" size="sm">
            {rows.length}
          </Badge>
        )}
      </div>

      {contacts === undefined && (
        <p className="text-sm text-muted-foreground">Loading contacts…</p>
      )}

      {contacts !== undefined && rows.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No associated contacts for this pre-appointment job.
        </p>
      )}

      {rows.length > 0 && (
        <div className="grid gap-1">
          {rows.slice(0, CONTACT_PREVIEW_LIMIT).map((contact) => {
            const TypeIcon = CONTACT_TYPE_META[contact.contactType].icon;
            const roles = distinctRoles(contact);
            return (
              <button
                key={String(contact._id)}
                type="button"
                className="flex w-full items-center gap-2 rounded-lg border bg-card px-3 py-2 text-left transition-colors hover:bg-muted/60"
                onClick={() => onOpenContact(contact._id)}
                title={`Open ${contact.displayName}'s record and correspondence`}
              >
                <TypeIcon className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {contact.displayName}
                </span>
                {roles.slice(0, 2).map((role) => (
                  <Badge
                    key={role}
                    variant="soft"
                    color={ROLE_META[role].badgeColor}
                    size="sm"
                    className="shrink-0"
                  >
                    {ROLE_META[role].label}
                  </Badge>
                ))}
              </button>
            );
          })}
        </div>
      )}

      {contacts !== undefined && (
        <button
          type="button"
          className="flex w-fit items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
          onClick={() =>
            navigate({
              to: "/dashboard/jobs/$jobId/contacts",
              params: { jobId },
            })
          }
        >
          <ExternalLink className="size-3" />
          View all contacts
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Conflict checks
// ---------------------------------------------------------------------------

function ConflictChecksSection({
  jobId,
  entityName,
  orgId,
}: {
  jobId: Id<"jobs">;
  entityName: string;
  orgId: Id<"organizations">;
}) {
  const { data: checks } = useQuery(
    convexQuery(api.preAppointment.conflicts.listForJob, { jobId }),
  );
  // The other entities the check covers (MOR-1419) — a head company's
  // subsidiaries, say. Empty for the ordinary single-entity check.
  const [entities, setEntities] = useState<ConflictEntityDraft[]>([]);
  // The parties the firm already knows about — entity, officers and related
  // parties from the corporate searches, and the referrer — across the
  // whole group once further entities are picked.
  const suggestedArgs = useMemo(
    () =>
      entities.length > 0
        ? { jobId, additionalEntities: toEntityInputs(entities) }
        : { jobId },
    [jobId, entities],
  );
  const { data: suggested, error: suggestedError } = useQuery(
    convexQuery(
      api.preAppointment.conflicts.getSuggestedParties,
      suggestedArgs,
    ),
  );
  const createCheck = useMutation(api.preAppointment.conflicts.create);

  const [newCheckOpen, setNewCheckOpen] = useState(false);
  // MOR-1846 — the draft follows the job data until the practitioner edits
  // it (searches often finish after the composer opens), then asks before
  // replacing their text.
  const {
    description,
    setDescription,
    updateAvailable: briefUpdateAvailable,
    ready: briefReady,
    applyLatest: applyLatestBrief,
    keepEdits: keepBriefEdits,
    reset: resetBriefDraft,
  } = useConflictBriefDraft(suggested?.text, newCheckOpen);
  const [creating, setCreating] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  // Files that ride along with the check (MOR-939), picked from the job
  // library with the same Finder the job chat uses.
  const [attachedDocs, setAttachedDocs] = useState<ChatDocPick[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Who gets polled (MOR-991): null = everyone in the firm (the default),
  // otherwise the members the practitioner ticked.
  const [recipients, setRecipients] = useState<Set<Id<"users">> | null>(null);
  const [viewDocId, setViewDocId] = useState<Id<"documents"> | null>(null);

  const { openChecks, closedChecks } = splitConflictChecksByStatus(checks);
  // One open check per job (enforced in `create`): a second poll would split
  // the firm's votes. Chasing non-responders is a resend on the open card.
  const hasOpenCheck = openChecks.length > 0;

  const handleCreate = async () => {
    if (!description.trim()) return;
    // Never send a draft that hasn't caught up with the entity list (F02):
    // the Send button is disabled while the brief loads, but a click that
    // lands in the same tick as an entity change must not slip through.
    if (!briefReady) {
      toast.error(
        "The brief is still updating for the entities you chose — wait for it to finish, then send.",
      );
      return;
    }
    setCreating(true);
    try {
      await createCheck({
        jobId,
        description: description.trim(),
        attachedDocumentIds:
          attachedDocs.length > 0
            ? attachedDocs.map((d) => d.documentId)
            : undefined,
        recipientUserIds: recipients ? [...recipients] : undefined,
        additionalEntities:
          entities.length > 0 ? toEntityInputs(entities) : undefined,
      });
      resetBriefDraft();
      setAttachedDocs([]);
      setRecipients(null);
      setEntities([]);
      setNewCheckOpen(false);
      toast.success(
        recipients
          ? `Conflict check started — ${recipients.size} member${recipients.size === 1 ? " has" : "s have"} been emailed a vote link.`
          : "Conflict check started — every firm member has been emailed a vote link.",
      );
    } catch (err) {
      toast.error(
        extractErrorMessage(err, "Could not start the conflict check."),
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="grid gap-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <ShieldCheck className="size-4" />
          Conflict checks
        </h3>
        {!hasOpenCheck && (
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => setNewCheckOpen((v) => !v)}
          >
            <Mail className="size-3.5" />
            Run check
          </Button>
        )}
      </div>

      {newCheckOpen && !hasOpenCheck && (
        <div className="grid gap-2 rounded-lg border bg-muted/40 p-3">
          {/* Which entities the check covers comes first: it decides what
              the brief below is pre-filled with (MOR-1419). */}
          <ConflictEntityPicker
            orgId={orgId}
            jobId={jobId}
            anchorName={entityName}
            entities={entities}
            onChange={setEntities}
          />
          <div className="flex items-center justify-between gap-2">
            <Label htmlFor="conflict-desc">Parties to check</Label>
            {suggested && (
              <button
                type="button"
                className="flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                onClick={applyLatestBrief}
              >
                <RefreshCw className="size-3" />
                Refill from job data
              </button>
            )}
          </div>
          <Textarea
            id="conflict-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the entity, directors, related parties, and the referrer so colleagues can check their engagements…"
            rows={8}
          />
          {briefUpdateAvailable && (
            <div
              role="status"
              className="grid gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
            >
              <p className="flex items-center gap-1.5 font-medium">
                <ShieldAlert className="size-3.5 shrink-0" />
                Job data has changed — review updates
              </p>
              <p>
                The searches have added or changed details since you edited this
                brief. Use the updated brief (your edits will be replaced) or
                keep your edits as they are.
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={applyLatestBrief}>
                  Use updated brief
                </Button>
                <Button size="sm" variant="ghost" onClick={keepBriefEdits}>
                  Keep my edits
                </Button>
              </div>
            </div>
          )}
          {suggestedError && (
            <p role="alert" className="text-xs text-red-600">
              Couldn't build the brief for these entities:{" "}
              {extractErrorMessage(
                suggestedError,
                "the job data could not be read.",
              )}
            </p>
          )}
          {!suggested && !suggestedError && (
            <p
              role="status"
              className="flex items-center gap-1.5 text-xs text-muted-foreground"
            >
              <Loader2 className="size-3 shrink-0 animate-spin" />
              Updating the brief for the entities you chose…
            </p>
          )}
          {suggested && (
            <p className="text-xs text-muted-foreground">
              {suggested.contactCount > 0
                ? `Pre-filled with ${suggested.contactCount} part${suggested.contactCount === 1 ? "y" : "ies"} from this job's searches${suggested.referrerName ? " and its referrer" : ""}. Edit before sending.`
                : "No officers or related parties have been extracted yet — run the company searches on this job, or add the parties by hand."}
            </p>
          )}
          {suggested &&
            (suggested.securedParties.length > 0 ||
              suggested.securedPartiesProcessing) && (
              <div className="grid gap-1.5 rounded-md border bg-background p-2">
                {/* Secured creditors are only counted in the brief text, so
                    surface the captured names here for the practitioner to
                    check — and flag when the platform is still gathering them
                    from the PPSR searches so a short list isn't mistaken for a
                    final one (MOR-992). */}
                {suggested.securedParties.length > 0 ? (
                  <ConflictPartyGroup
                    list={{
                      label: "Secured parties",
                      names: suggested.securedParties.map((p) => p.name),
                      parties: suggested.securedParties,
                    }}
                  />
                ) : (
                  <p className="px-1.5 text-xs font-medium">Secured parties</p>
                )}
                {suggested.securedPartiesProcessing && (
                  <p className="flex items-center gap-1.5 px-1.5 text-[11px] text-muted-foreground">
                    <Loader2 className="size-3 shrink-0 animate-spin" />
                    Still capturing secured parties from the PPSR searches —
                    this list may be incomplete. Check back before sending.
                  </p>
                )}
                {suggested.securedRegistrationsTruncated && (
                  <p className="flex items-center gap-1.5 px-1.5 text-[11px] text-amber-700">
                    <ShieldAlert className="size-3 shrink-0" />
                    This job has more than 500 active PPSR registrations — only
                    the first 500 are shown, snapshotted, and emailed.
                  </p>
                )}
              </div>
            )}
          <div className="grid gap-1.5">
            <Button
              size="sm"
              variant="outline"
              className="w-fit gap-1.5"
              onClick={() => setPickerOpen(true)}
            >
              <Paperclip className="size-3.5" />
              Attach files
            </Button>
            {attachedDocs.length > 0 && (
              /*
                `min-w-0`: as a grid item this row would otherwise take the
                nowrap filename as its minimum width, widening the whole card
                past the sheet and pushing Cancel / Send to firm off-screen.
              */
              <div className="flex min-w-0 flex-wrap gap-1.5">
                {attachedDocs.map((doc) => (
                  <span
                    key={String(doc.documentId)}
                    className="flex max-w-full items-center gap-1 rounded-md border bg-background px-1.5 py-0.5 text-xs"
                  >
                    <FileText className="size-3 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 truncate">{doc.fileName}</span>
                    <button
                      type="button"
                      className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                      aria-label={`Remove ${doc.fileName}`}
                      onClick={() =>
                        setAttachedDocs((prev) =>
                          prev.filter((d) => d.documentId !== doc.documentId),
                        )
                      }
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Files are added as attachments to the conflict check email.
            </p>
          </div>
          <ConflictRecipientPicker
            orgId={orgId}
            selected={recipients}
            onChange={setRecipients}
          />
          <p className="text-xs text-muted-foreground">
            Each recipient gets an email with a one-click vote link — yes/no
            plus a reason. No login needed.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setNewCheckOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={
                creating ||
                !description.trim() ||
                recipients?.size === 0 ||
                // MOR-1846 — the job data changed after the practitioner
                // edited the brief; they choose which version goes out.
                briefUpdateAvailable ||
                // F02 — the suggested brief for the current entity list is
                // still loading, or hasn't been written into the draft yet.
                !briefReady ||
                // The check snapshots secured parties once, at creation
                // (MOR-992) — sending while the PPSR searches or the
                // security-interests agent are still running would freeze
                // that snapshot before it's complete. `create` enforces the
                // same condition server-side.
                suggested?.securedPartiesProcessing === true
              }
              onClick={() => void handleCreate()}
            >
              {creating
                ? "Sending…"
                : recipients
                  ? `Send to ${recipients.size} member${recipients.size === 1 ? "" : "s"}`
                  : "Send to firm"}
            </Button>
          </div>
        </div>
      )}

      {(checks ?? []).length === 0 && !newCheckOpen && (
        <p className="text-sm text-muted-foreground">
          No conflict check run yet. Run check to poll every member of the firm
          before confirming appointment.
        </p>
      )}

      {openChecks.map((check) => (
        <ConflictCheckCard
          key={String(check._id)}
          check={check}
          jobId={jobId}
          onViewDocument={setViewDocId}
        />
      ))}

      {/* Closed checks stay out of the way but remain auditable. */}
      {closedChecks.length > 0 && (
        <div className="grid gap-2">
          <button
            type="button"
            className="flex w-fit items-center gap-1.5 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            onClick={() => setShowClosed((v) => !v)}
          >
            {showClosed ? (
              <ChevronDown className="size-3.5" />
            ) : (
              <ChevronRight className="size-3.5" />
            )}
            {formatClosedToggleLabel(closedChecks.length, showClosed)}
          </button>
          {showClosed &&
            closedChecks.map((check) => (
              <ConflictCheckCard
                key={String(check._id)}
                check={check}
                jobId={jobId}
                onViewDocument={setViewDocId}
              />
            ))}
        </div>
      )}

      <JobChatDocumentPicker
        jobId={jobId}
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title="Add files to conflict check"
        description="Select file(s) from the job library or upload directly to add to conflict check email."
        onSelect={(picked) =>
          setAttachedDocs((prev) => {
            const seen = new Set(prev.map((d) => String(d.documentId)));
            return [
              ...prev,
              ...picked.filter((d) => !seen.has(String(d.documentId))),
            ];
          })
        }
      />

      <DocumentDetail
        documentId={viewDocId}
        isViewer={false}
        open={viewDocId !== null}
        onClose={() => setViewDocId(null)}
      />
    </div>
  );
}

function ConflictCheckCard({
  check,
  jobId,
  onViewDocument,
}: {
  check: ConflictCheck;
  /** The job whose panel this is — a group check may anchor elsewhere. */
  jobId: Id<"jobs">;
  onViewDocument: (documentId: Id<"documents">) => void;
}) {
  const submitVote = useMutation(api.preAppointment.conflicts.submitVote);
  const withdrawVote = useMutation(api.preAppointment.conflicts.withdrawVote);
  const closeCheck = useMutation(api.preAppointment.conflicts.close);
  const sendReminder = useMutation(
    api.preAppointment.conflicts.sendManualReminder,
  );
  const resendToPending = useMutation(
    api.preAppointment.conflicts.resendToPending,
  );

  const [reason, setReason] = useState("");
  const [voting, setVoting] = useState<"yes" | "no" | null>(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [remindingId, setRemindingId] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [showResponses, setShowResponses] = useState(false);
  // Which member's vote trail is expanded (MOR-965) — the history is on the
  // record here, not only on the voter's own link.
  const [expandedResponseId, setExpandedResponseId] = useState<string | null>(
    null,
  );
  // A closed check reads as a result, so it defaults to votes-cast only.
  const [hideNoResponse, setHideNoResponse] = useState(
    check.status === "closed",
  );
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);

  const myResponse = findMyResponse(check);

  const handleVote = async (vote: "yes" | "no") => {
    setVoting(vote);
    try {
      await submitVote({
        conflictCheckId: check._id,
        vote,
        reason: reason.trim() || undefined,
      });
      setReason("");
      toast.success("Vote recorded.");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not record your vote."));
    } finally {
      setVoting(null);
    }
  };

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      await withdrawVote({ conflictCheckId: check._id });
      toast.success(
        "Vote withdrawn — you're back in the no-response list until you vote again.",
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not withdraw your vote."));
    } finally {
      setWithdrawing(false);
    }
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      await closeCheck({ conflictCheckId: check._id });
      setConfirmClose(false);
      toast.success(
        "Conflict check closed — no further votes, and it drops out of the open list.",
      );
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not close the check."));
    } finally {
      setClosing(false);
    }
  };

  const handleRemind = async (responseId: string, voterName: string) => {
    setRemindingId(responseId);
    try {
      await sendReminder({
        responseId: responseId as Id<"conflictCheckResponses">,
      });
      toast.success(`Reminder sent to ${voterName}.`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not send the reminder."));
    } finally {
      setRemindingId(null);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const count = await resendToPending({ conflictCheckId: check._id });
      toast.success(formatResendToastMessage(count));
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not resend the check."));
    } finally {
      setResending(false);
    }
  };

  const voted = votedResponses(check);
  const visibleResponses = visibleConflictResponses(check, hideNoResponse);

  return (
    <div className="grid gap-2.5 rounded-lg border p-3">
      {/*
        `min-w-0`: this row is a grid item, so a nowrap attachment filename
        below would otherwise set its minimum width and widen the card past
        the sheet (MOR-1305).
      */}
      <div className="flex min-w-0 items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">
            Started by {check.createdByName} ·{" "}
            {format(check._creationTime, "d MMM yyyy")}
            {check.jobId !== jobId && check.entities[0] && (
              <> · run from {check.entities[0].name}</>
            )}
          </p>
          {/* A group check (MOR-1419) names every entity it covers up top —
              the brief below runs to a block per entity. */}
          {check.entities.length > 1 && (
            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs">
              <Building2 className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="text-muted-foreground">
                Covers {check.entities.length} entities:
              </span>
              {check.entities.map((entity, index) => (
                <span
                  key={entity.jobId ? String(entity.jobId) : `name:${index}`}
                  className={cn(
                    "font-medium",
                    entity.jobId === jobId && "underline underline-offset-2",
                  )}
                >
                  {entity.name}
                  {index < check.entities.length - 1 && ","}
                </span>
              ))}
            </p>
          )}
          <ConflictBriefView description={check.description} className="mt-1" />
          <ConflictPartySchedule
            partyLists={check.partyLists}
            showAttachmentNote
            className="mt-2"
          />
          {check.attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {check.attachments.map((attachment) => (
                <button
                  key={String(attachment.documentId)}
                  type="button"
                  className="flex max-w-full items-center gap-1 rounded-md border bg-background px-1.5 py-0.5 text-xs hover:bg-muted"
                  title="View document"
                  onClick={() => onViewDocument(attachment.documentId)}
                >
                  <FileText className="size-3 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 truncate">
                    {attachment.fileName}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
        <Badge
          variant={check.status === "open" ? "default" : "outline"}
          size="sm"
          className="shrink-0"
        >
          {check.status === "open" ? "Open" : "Closed"}
        </Badge>
      </div>

      <ConflictVoteBar
        yes={check.tally.yes}
        no={check.tally.no}
        pending={check.tally.pending}
        hideNoResponse={hideNoResponse}
        onHideNoResponseChange={setHideNoResponse}
        idPrefix={String(check._id)}
      />

      <button
        type="button"
        className="w-fit text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
        onClick={() => setShowResponses((v) => !v)}
      >
        {showResponses ? "Hide responses" : "Show responses"}
      </button>

      {showResponses && (
        <div className="grid gap-1.5 rounded-md bg-muted/40 p-2">
          {visibleResponses.length === 0 && (
            <p className="text-xs text-muted-foreground">No votes cast yet.</p>
          )}
          {visibleResponses.map((r) => (
            <div key={String(r._id)} className="flex items-start gap-2 text-xs">
              {r.vote === "yes" ? (
                <XCircle className="mt-0.5 size-3.5 shrink-0 text-red-600" />
              ) : r.vote === "no" ? (
                <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-green-600" />
              ) : (
                <span
                  className="mt-1 size-2.5 shrink-0 rounded-full border border-muted-foreground/40"
                  title={
                    r.emailStatus === "failed"
                      ? "Email failed to send"
                      : "Awaiting response"
                  }
                />
              )}
              <div className="min-w-0 flex-1">
                {r.voteHistory.length > 0 ? (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-medium underline-offset-2 hover:underline"
                    title="Show this member's vote history"
                    onClick={() =>
                      setExpandedResponseId((prev) =>
                        prev === String(r._id) ? null : String(r._id),
                      )
                    }
                  >
                    {expandedResponseId === String(r._id) ? (
                      <ChevronDown className="size-3" />
                    ) : (
                      <ChevronRight className="size-3" />
                    )}
                    {r.voterName}
                    <span className="text-[10px] font-normal text-muted-foreground">
                      ({r.voteHistory.length})
                    </span>
                  </button>
                ) : (
                  <span className="font-medium">{r.voterName}</span>
                )}
                {r.vote === undefined && r.emailStatus === "failed" && (
                  <span className="ml-1 text-red-600">(email failed)</span>
                )}
                {describeWithdrawal(r) !== null && (
                  <span className="ml-1 text-muted-foreground">
                    (withdrew their vote {describeWithdrawal(r)})
                  </span>
                )}
                {describeReminder(r) !== null && (
                  <span className="ml-1 text-muted-foreground">
                    (reminded {describeReminder(r)})
                  </span>
                )}
                {r.reason && (
                  <p className="text-muted-foreground">{r.reason}</p>
                )}
                {expandedResponseId === String(r._id) &&
                  r.voteHistory.length > 0 && (
                    <ul className="mt-1 flex flex-col gap-0.5 border-l-2 border-muted-foreground/20 pl-2">
                      {[...r.voteHistory].reverse().map((entry, index) => (
                        <li key={index} className="text-muted-foreground">
                          {formatVoteHistoryLine(entry)}
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
              {check.status === "open" && r.vote === undefined && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 shrink-0 gap-1 px-2 text-xs"
                  disabled={remindingId !== null}
                  onClick={() => void handleRemind(String(r._id), r.voterName)}
                  title="Email this member their vote link again"
                >
                  <BellRing className="size-3" />
                  {remindingId === String(r._id) ? "Sending…" : "Remind"}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {check.status === "open" && (
        <div className="grid gap-2 border-t pt-2.5">
          <div className="flex flex-wrap items-center justify-between gap-1">
            <p className="text-xs font-medium">
              {myResponse?.vote
                ? `Your vote: ${voteLabel(myResponse.vote)} — you can change it while the check is open.`
                : "Your vote"}
            </p>
            {myResponse?.vote && (
              <Button
                size="sm"
                variant="ghost"
                className="h-6 gap-1 px-2 text-xs text-muted-foreground"
                disabled={withdrawing}
                onClick={() => void handleWithdraw()}
                title="Clear your vote — you go back to the no-response list"
              >
                <Undo2 className="size-3" />
                {withdrawing ? "Withdrawing…" : "Withdraw vote"}
              </Button>
            )}
          </div>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required when flagging a conflict)"
            rows={2}
          />
          {/* Wraps rather than widening the panel when all four fit poorly. */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={voting !== null}
              onClick={() => void handleVote("no")}
            >
              <CheckCircle2 className="size-3.5 text-green-600" />
              {voting === "no" ? "Saving…" : "No conflict"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={voting !== null || !reason.trim()}
              onClick={() => void handleVote("yes")}
            >
              <ShieldAlert className="size-3.5 text-red-600" />
              {voting === "yes" ? "Saving…" : "Flag conflict"}
            </Button>
            <div className="ml-auto flex items-center gap-1">
              {check.tally.pending > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="gap-1.5"
                  disabled={resending}
                  onClick={() => void handleResend()}
                  title={`Email the vote link again to the ${check.tally.pending} member${check.tally.pending === 1 ? "" : "s"} who haven't voted`}
                >
                  <Mail className="size-3.5" />
                  {resending ? "Resending…" : `Resend (${check.tally.pending})`}
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="gap-1.5"
                onClick={() => setConfirmClose(true)}
              >
                <Lock className="size-3.5" />
                Close
              </Button>
            </div>
          </div>
          {voted.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Waiting for the first responses to arrive.
            </p>
          )}
        </div>
      )}

      {check.status === "closed" && check.closedAt && (
        <p className="border-t pt-2 text-xs text-muted-foreground">
          Closed {format(check.closedAt, "d MMM yyyy")} — no further votes can
          be cast.
        </p>
      )}

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Close this conflict check?</AlertDialogTitle>
            <AlertDialogDescription>
              Voting stops immediately and the check moves out of the open list
              into the closed section, where it stays on the record.
              {check.tally.pending > 0 && (
                <>
                  {" "}
                  {check.tally.pending} member
                  {check.tally.pending === 1 ? " has" : "s have"} not responded
                  yet — their vote will be recorded as no response.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={closing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={closing}
              onClick={(e) => {
                e.preventDefault();
                void handleClose();
              }}
            >
              {closing ? "Closing…" : "Close check"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
